import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '@heidi/design-system/popover';
import { type ReactNode } from 'react';
import { ClinicianSelectorCommand } from '../session-list/clinician-selector-command';
import { type SessionListHost } from '../session-list/session-list-host';
import {
  SessionListHostProvider,
  useSessionListHost,
} from '../session-list/session-list-host-context';

interface ClinicianSelectorPopoverProps {
  /** Controlled open state — owned by the toolbar CTA so the chevron menu can open it. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the picked clinician's member id after the popover closes. */
  onSelectClinician: (memberId: string) => void;
  /** The trigger — the toolbar CTA's main "New" button. */
  children: ReactNode;
  /**
   * Popover alignment relative to the trigger. Defaults to `'end'` for the
   * right-aligned toolbar CTA; the left-aligned sidebar button passes `'start'`.
   */
  contentAlign?: 'start' | 'end';
  /**
   * Side the popover opens on relative to the trigger. Defaults to the DS
   * `PopoverContent` default (`'bottom'`); the sidebar "+" passes `'right'`
   * (desktop) so the picker lands where its dropdown menu does.
   */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Distance in px from the trigger. Defaults to the DS `PopoverContent` default. */
  sideOffset?: number;
  /**
   * Render `children` as a positioning anchor only, not a click-to-open
   * trigger. Use when an external control owns opening (e.g. the sidebar "+"
   * where a dropdown menu item flips `open`) so the popover neither injects a
   * toggle onClick nor overwrites the child's own menu ARIA. Defaults to
   * trigger behaviour for the toolbar CTA / empty-state / sidebar button.
   */
  anchorOnly?: boolean;
}

/**
 * Popover that lets an assistant/admin pick which clinician a new session is for
 * (create-on-behalf-of). Extracted from the old session-list header so the new
 * inline toolbar CTA can reuse the exact same selector — picking a clinician sets
 * `selectedReviewMemberAtom`, closes, then delegates to `onSelectClinician`.
 *
 * The account owner (self) is pinned above the Team group when present, showing
 * their name + avatar rather than "Me" (SCR-5441).
 */
export function ClinicianSelectorPopover({
  host,
  ...props
}: ClinicianSelectorPopoverProps & { host: SessionListHost }) {
  return (
    <SessionListHostProvider host={host}>
      <ClinicianSelectorPopoverBody {...props} />
    </SessionListHostProvider>
  );
}

function ClinicianSelectorPopoverBody({
  open,
  onOpenChange,
  onSelectClinician,
  children,
  contentAlign = 'end',
  side,
  sideOffset,
  anchorOnly = false,
}: ClinicianSelectorPopoverProps) {
  const host = useSessionListHost();
  const { clinicians, isLoading, currentUserId } = host.identity.useTeamClinicianList();
  const setSelectedReviewMember = host.identity.useSetSelectedReviewMember();

  const handleSelect = (memberId: string) => {
    onOpenChange(false);
    setSelectedReviewMember(memberId);
    onSelectClinician(memberId);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      {anchorOnly ? (
        <PopoverAnchor asChild>{children}</PopoverAnchor>
      ) : (
        <PopoverTrigger asChild>{children}</PopoverTrigger>
      )}
      <PopoverContent
        className="w-64 p-0"
        align={contentAlign}
        side={side}
        sideOffset={sideOffset}
      >
        <ClinicianSelectorCommand
          clinicians={clinicians}
          currentUserId={currentUserId}
          isLoading={isLoading}
          onSelect={handleSelect}
        />
      </PopoverContent>
    </Popover>
  );
}
